import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { usePushSubscription } from '../hooks/usePushSubscription';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Bell,
  BellOff,
  Link2,
  Mail,
  MoreVertical,
  Pause,
  Pencil,
  Play,
  RefreshCw,
  SearchX,
  Send,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import { TIER } from '../utils/tier-constants';
import { getMaxSubscriptions, isAdminUser } from '../utils/tier-utils';
import { formatPrice as formatEuroAmount } from '../utils/money-utils';
import { PLANS } from '../config/plans';
import { cn } from '../utils/cn';
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  Dialog,
  EmptyState,
  IconButton,
  Menu,
  PageHeader,
  Spinner,
  buttonClasses,
  inlineLinkClass,
} from '../components/ui';
import { useSubscriptions, useDeleteSubscription, useUpdateSubscriptionStatus } from '../hooks/queries/useSubscriptions';
import { useHealth } from '../hooks/queries/useHealth';
import { useUpgradeModal, SuggestedTier } from '../contexts/UpgradeModalContext';
import { Subscription } from '../api/subscriptions';
import { PasskeySetupAction, usePasskeyStatus } from '../components/PasskeyPrompt';
import { PWAInstallAdvice } from '../components/PWAInstallAdvice';
import { SetupChecklist, SetupChecklistItem } from '../components/SetupChecklist';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useLocationToast } from '../hooks/useLocationToast';
import { defaultSubscriptionTitle } from '../utils/subscriptionTitle';
import { buildAreaLabel } from '../utils/areaLabel';
import { useGeoData } from '../contexts/GeoDataContext';
import { describeCoreFilters, describeFlagFilters } from '../utils/subscriptionDescription';

const TIER_LABEL: Record<string, string> = { free: 'Free', pro: 'Pro', premium: 'Premium', admin: 'Admin' };

/** Display title for a subscription: an explicit name if the backend ever sends one, else its zones/PLZ. */
function getSubscriptionTitle(subscription: Subscription): React.ReactNode {
  const name = (subscription.uiMetadata as any)?.name;
  if (name) return name;

  return defaultSubscriptionTitle(subscription.uiMetadata?.zones as any, subscription.zipCodes);
}

/** "N PLZ | bis 1.200 € warm | 2–3 Zi. | ab 50 m²" — same separator style as `FactLine`, skipping missing facts. */
function subscriptionFactItems(subscription: Subscription): string[] {
  const items: string[] = [];
  if (subscription.zipCodes?.length) items.push(`${subscription.zipCodes.length} PLZ`);
  return [...items, ...describeCoreFilters(subscription.filters as any)];
}

/** Every zone label of the search (the title only shows the first three). */
function subscriptionAreaText(subscription: Subscription): string | null {
  const zones = (subscription.uiMetadata?.zones ?? []) as Array<{ l?: string; label?: string; i?: string; id?: string }>;
  const labels = zones.map((z) => z.l || z.label || z.i || z.id).filter(Boolean);
  return labels.length > 3 ? labels.join(', ') : null;
}

/** Renders a list of strings with the shared fact-line vertical-rule separator (never "·"). */
const SeparatedLine: React.FC<{ items: React.ReactNode[]; className?: string }> = ({ items, className }) => (
  <p className={cn('tabular text-sm', className)}>
    {items.map((item, index) => (
      <span key={index} className={cn('inline-block whitespace-nowrap', index > 0 && 'border-l border-line-strong/60 pl-2 ml-2')}>
        {item}
      </span>
    ))}
  </p>
);

const DashboardHome: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSubscribed } = usePushSubscription();
  const { openUpgradeModal } = useUpgradeModal();
  const passkeyStatus = usePasskeyStatus();
  const toast = useLocationToast();

  const { data: subscriptions = [], isLoading, refetch, error } = useSubscriptions(user?.userId);
  // Zone definitions name each search's areas like the Telegram bot does ("Berlin Mitte (3 PLZ)").
  // Same file the editor loads, so it is usually cached already.
  const { zoneDefinitions, loadZones } = useGeoData();
  useEffect(() => {
    if (subscriptions.length > 0) loadZones();
  }, [subscriptions.length, loadZones]);

  const deleteMutation = useDeleteSubscription();
  const updateStatusMutation = useUpdateSubscriptionStatus();
  const { data: health } = useHealth();
  const isMaintenance = health?.maintenanceMode && user?.tier !== TIER.ADMIN;

  const [verificationMessage, setVerificationMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [connectModalSubId, setConnectModalSubId] = useState<string | null>(null);
  const [skipGroupWarningChecked, setSkipGroupWarningChecked] = useState(false);
  const [deleteConfirmState, setDeleteConfirmState] = useState<{ id: string; message: string } | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const hasPrivateSubscription = subscriptions.some(s => s.notificationTargets?.telegram?.chatType === 'private');

  // Redirect to Feed if push is enabled on this device
  useEffect(() => {
    if (isSubscribed === true && !location.state?.noRedirect) {
      console.log('[DashboardHome] Push active, redirecting to Feed');
      navigate('/feed', { replace: true });
    }
  }, [isSubscribed, navigate, location.state]);

  useEffect(() => {
    // Auto-refresh when coming back to the tab
    let refreshTimer: NodeJS.Timeout;
    const onFocus = () => {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => {
            if (user?.userId) {
                refetch();
            }
        }, 3000); // 3 second debounce
    };
    window.addEventListener('focus', onFocus);

    // Explicit ESC key handler for modal
    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setConnectModalSubId(null);
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('keydown', onKeyDown);
    };
  }, [user?.userId, refetch]);

  const handleResendVerification = async () => {
    setIsVerifying(true);
    try {
      await api.post(`/user/resend-verification`, {
        userId: user?.userId
      });
      setVerificationMessage('E-Mail gesendet!');
      setTimeout(() => setVerificationMessage(''), 5000);
    } catch (err: any) {
      setVerificationMessage('Fehler beim Senden.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDelete = async (id: string) => {
    const subscription = subscriptions.find(sub => sub.subscriptionId === id);
    const hasTelegramChat = subscription?.notificationTargets?.telegram?.chatId;
    const chatTitle = subscription?.notificationTargets?.telegram?.chatTitle;
    const isGroupChat = hasTelegramChat && hasTelegramChat < 0;

    let confirmMessage = 'Möchtest du diese Suche wirklich löschen?';

    if (isGroupChat) {
      confirmMessage += `\n\nDer Bot wird die Telegram-Gruppe "${chatTitle}" verlassen.`;
    } else if (hasTelegramChat) {
      const isMainPrivate = subscription?.notificationTargets?.telegram?.chatType === 'private';
      if (isMainPrivate && subscriptions.length > 1) {
        alert('Bitte lösche zuerst alle anderen Suchen, bevor du die Haupt-Suche löschst.');
        return;
      }
      confirmMessage += `\n\nDu erhältst eine Abschiedsnachricht im Telegram-Chat.`;
    }

    // Use a custom in-DOM confirm dialog instead of window.confirm(): native confirm()
    // is unreliable/silently suppressed in embedded WebViews (e.g. the Telegram Mini-App
    // WebView used by TelegramSubscriptionPage.tsx), which would make this action appear
    // to do nothing at all (see issue #365).
    setDeleteConfirmState({ id, message: confirmMessage });
  };

  const executeDelete = async (id: string) => {
    setDeleteConfirmState(null);
    try {
      await deleteMutation.mutateAsync(id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Löschen des Abonnements');
    }
  };

  const handleToggleEnabled = async (id: string, enabled: boolean) => {
    try {
      await updateStatusMutation.mutateAsync({ id, enabled: !enabled });
    } catch (err) {
      alert('Fehler beim Aktualisieren des Status');
    }
  };

  const handleConnectTelegramClick = (subscriptionId: string) => {
    if (!user?.telegramId || !hasPrivateSubscription) {
      // Force Private Chat Flow
      const botName = process.env.REACT_APP_TELEGRAM_BOT_NAME || 'schnellerwohnen_bot';
      window.open(`https://t.me/${botName}?start=search_${subscriptionId}`, '_blank');
    } else {
      // Check if user wants to skip confirmation
      const skipWarning = localStorage.getItem('schnellerwohnen_skip_group_warning');
      if (skipWarning === 'true') {
        const botName = process.env.REACT_APP_TELEGRAM_BOT_NAME || 'schnellerwohnen_bot';
        window.open(`https://t.me/${botName}?startgroup=search_${subscriptionId}`, '_blank');
      } else {
        setConnectModalSubId(subscriptionId);
      }
    }
  };

  const handleConfirmConnectGroup = () => {
    if (skipGroupWarningChecked) {
      localStorage.setItem('schnellerwohnen_skip_group_warning', 'true');
    }
    const botName = process.env.REACT_APP_TELEGRAM_BOT_NAME || 'schnellerwohnen_bot';
    window.open(`https://t.me/${botName}?startgroup=search_${connectModalSubId}`, '_blank');
    setConnectModalSubId(null);
  };

  const currentLimit = getMaxSubscriptions(user?.tier, user?.isAdmin);
  const canAddMore = subscriptions.length < currentLimit;
  const isAdmin = isAdminUser(user);
  const suggestedUpgradeTier: SuggestedTier = user?.tier === TIER.PRO ? 'premium' : 'pro';

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const minDelay = new Promise(resolve => setTimeout(resolve, 500));
    try {
      await Promise.all([refetch(), minDelay]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const setupItems: SetupChecklistItem[] = [
    { id: 'account', label: 'Konto angelegt', done: true },
    {
      id: 'email',
      label: 'E-Mail bestätigen',
      done: !user?.email || !!user?.emailVerified,
      action: verificationMessage ? (
        <span className="text-sm text-muted">{verificationMessage}</span>
      ) : (
        <Button type="button" variant="secondary" size="sm" onClick={handleResendVerification} disabled={isVerifying}>
          {isVerifying ? 'Sende...' : 'Link erneut senden'}
        </Button>
      ),
    },
    {
      id: 'channel',
      label: 'Kanal verbinden',
      done: !!user?.telegramId || !!(user?.devices && Object.keys(user.devices).length > 0),
      action: (
        <Link to="/dashboard/settings" className={buttonClasses('secondary', 'sm')}>
          Verbinden
        </Link>
      ),
    },
    ...(passkeyStatus.supported
      ? [
          {
            id: 'passkey',
            label: 'Passkey einrichten',
            done: !!passkeyStatus.hasPasskey,
            optional: true,
            action: <PasskeySetupAction onRegistered={passkeyStatus.refresh} />,
          },
        ]
      : []),
  ];

  const headerAction = subscriptions.length === 0 ? undefined : canAddMore ? (
    <Link to="/dashboard/subscription/new" className={buttonClasses('primary')}>
      Neue Suche
    </Link>
  ) : (
    <button type="button" className={buttonClasses('primary')} onClick={() => openUpgradeModal(suggestedUpgradeTier)}>
      Neue Suche
    </button>
  );

  return (
    <div className="mx-auto w-full max-w-wide">
      <PageHeader
        title="Deine Suchen"
        actions={
          <>
            {subscriptions.length > 0 && (
              <IconButton
                label="Suchen aktualisieren"
                icon={<RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} aria-hidden="true" />}
                variant="ghost"
                onClick={handleRefresh}
                disabled={isRefreshing}
              />
            )}
            {headerAction}
          </>
        }
      />

      <div className="mt-6">
        {toast.message && (
          <Banner tone="success" onDismiss={toast.dismiss} className="mb-4">
            {toast.message}
          </Banner>
        )}

        <SetupChecklist items={setupItems} onDismiss={() => {}} />

        <PWAInstallAdvice />

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size={28} />
          </div>
        ) : error ? (
          <Banner tone="danger">{(error as Error).message || 'Ein Fehler ist aufgetreten.'}</Banner>
        ) : subscriptions.length === 0 ? (
          <EmptyState
            icon={<SearchX className="h-6 w-6" aria-hidden="true" />}
            title="Noch keine Suche"
            className="empty-state"
            action={
              <Link to="/dashboard/subscription/new" className={buttonClasses('primary')}>
                Erste Suche anlegen
              </Link>
            }
          >
            Leg deine erste Suche an. Wir melden uns, sobald etwas passt.
          </EmptyState>
        ) : (
          <div className="flex flex-col gap-4">
            {subscriptions.map((subscription) => {
              const title = getSubscriptionTitle(subscription);
              const factItems = subscriptionFactItems(subscription);
              const flagItems = describeFlagFilters(subscription.filters as any);
              const areaText = buildAreaLabel(subscription.zipCodes ?? [], zoneDefinitions, 4) ?? subscriptionAreaText(subscription);
              const daysLeft = subscription.expiresAt != null ? Math.ceil((subscription.expiresAt - Date.now()) / (24 * 60 * 60 * 1000)) : null;
              const isExpired = daysLeft !== null && daysLeft <= 0;
              const status = isExpired
                ? { tone: 'danger' as const, label: 'abgelaufen' }
                : subscription.enabled
                ? { tone: 'success' as const, label: 'aktiv' }
                : { tone: 'neutral' as const, label: 'deaktiviert' };
              const isMainPrivateLocked = subscription.notificationTargets?.telegram?.chatType === 'private' && subscriptions.length > 1;
              const channels = subscription.notificationChannels ?? [];

              return (
                <Card
                  key={subscription.subscriptionId}
                  padding="md"
                  className={cn('subscription-card', !subscription.enabled && 'inactive')}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate font-display text-lg font-semibold text-ink">{title}</h3>
                      {factItems.length > 0 ? (
                        <SeparatedLine items={factItems} className="mt-1 font-medium text-ink" />
                      ) : (
                        <p className="mt-1 text-sm text-muted">Keine Filter</p>
                      )}
                      {/* Desktop has room for the full picture: every area and the yes/no filters. */}
                      {areaText && <p className="mt-1 line-clamp-2 text-sm text-muted">Gebiet: {areaText}</p>}
                      {flagItems.length > 0 && (
                        <div className="mt-2 hidden flex-wrap gap-1.5 md:flex">
                          {flagItems.map((item) => (
                            <Badge key={item} tone="neutral">{item}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <Badge tone={status.tone}>{status.label}</Badge>
                  </div>

                  <div className="mt-3 flex flex-col gap-2">
                    {channels.includes('telegram') && (
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span
                          className={cn(
                            'flex items-center gap-1.5 font-medium',
                            subscription.notificationTargets?.telegram?.chatId ? 'text-ink' : 'text-danger-text'
                          )}
                        >
                          <Send className="h-4 w-4" aria-hidden="true" /> Telegram
                        </span>
                        {!subscription.notificationTargets?.telegram?.chatId ? (
                          <>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              leftIcon={<Link2 className="h-4 w-4" aria-hidden="true" />}
                              onClick={() => handleConnectTelegramClick(subscription.subscriptionId)}
                              disabled={isMaintenance}
                            >
                              Telegram verbinden
                            </Button>
                            {subscription.uiMetadata?.originalTelegramGroup?.chatTitle && (
                              <span className="text-xs italic text-muted">
                                war {subscription.uiMetadata.originalTelegramGroup.chatType === 'private' ? 'privater Chat' : 'Gruppe'} „{subscription.uiMetadata.originalTelegramGroup.chatTitle}“
                              </span>
                            )}
                          </>
                        ) : (
                          <Badge tone="neutral" className="gap-1">
                            {subscription.notificationTargets.telegram.chatType === 'private' ? (
                              <User className="h-3.5 w-3.5" aria-hidden="true" />
                            ) : (
                              <Users className="h-3.5 w-3.5" aria-hidden="true" />
                            )}
                            {subscription.notificationTargets.telegram.chatTitle ||
                              (subscription.notificationTargets.telegram.chatType === 'private' ? 'Privater Chat' : 'Gruppe')}
                          </Badge>
                        )}
                      </div>
                    )}

                    {channels.includes('push') && (
                      <span className="flex items-center gap-1.5 text-sm text-muted">
                        <Bell className="h-4 w-4" aria-hidden="true" /> Push
                      </span>
                    )}

                    {channels.includes('email') && (
                      <span className="flex items-center gap-1.5 text-sm text-muted">
                        <Mail className="h-4 w-4" aria-hidden="true" /> E-Mail
                      </span>
                    )}

                    {channels.length === 0 && (
                      <span className="flex items-center gap-1.5 text-sm text-muted">
                        <BellOff className="h-4 w-4" aria-hidden="true" /> Keine Meldungen eingestellt
                      </span>
                    )}
                  </div>

                  {user?.tier === TIER.FREE && subscription.expiresAt != null && !isExpired && daysLeft !== null && (
                    <p className="mt-3 text-sm text-muted">noch {daysLeft} {daysLeft === 1 ? 'Tag' : 'Tage'} aktiv</p>
                  )}

                  {!isExpired && daysLeft !== null && daysLeft <= 3 && daysLeft > 0 && (
                    <Banner tone="warning" className="mt-3">
                      Läuft in {daysLeft} {daysLeft === 1 ? 'Tag' : 'Tagen'} ab.
                      {user?.tier === TIER.FREE && (
                        <>
                          {' '}
                          <Link to="/dashboard/upgrade" className="font-semibold underline">
                            Upgraden für unbegrenzte Suchen
                          </Link>
                        </>
                      )}
                    </Banner>
                  )}

                  {isExpired && (
                    <Banner tone="danger" className="mt-3">
                      Diese Suche ist abgelaufen und wird bald gelöscht.
                      {user?.tier === TIER.FREE && (
                        <>
                          {' '}
                          <Link to="/dashboard/upgrade" className="font-semibold underline">
                            Jetzt upgraden
                          </Link>
                        </>
                      )}
                    </Banner>
                  )}

                  <div className="mt-4 flex items-center justify-between border-t border-line pt-3">
                    <Link
                      to={`/dashboard/subscription/edit/${subscription.subscriptionId}`}
                      className={cn(buttonClasses('secondary', 'sm'), isMaintenance && 'pointer-events-none opacity-50')}
                      aria-disabled={isMaintenance}
                      onClick={(e) => {
                        if (isMaintenance) e.preventDefault();
                      }}
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                      Bearbeiten
                    </Link>
                    {/* md+: the menu's actions as direct buttons; phones keep the compact ⋯ menu. */}
                    <div className="hidden items-center gap-2 md:flex">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        leftIcon={subscription.enabled ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
                        disabled={isMaintenance}
                        onClick={() => handleToggleEnabled(subscription.subscriptionId, subscription.enabled)}
                      >
                        {subscription.enabled ? 'Deaktivieren' : 'Aktivieren'}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="delete text-danger-text hover:bg-danger-surface"
                        leftIcon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
                        title={isMainPrivateLocked ? 'Lösche zuerst alle anderen Suchen, bevor du die Haupt-Suche löschst.' : 'Löschen'}
                        disabled={isMaintenance || isMainPrivateLocked}
                        onClick={() => handleDelete(subscription.subscriptionId)}
                      >
                        Löschen
                      </Button>
                    </div>
                    <Menu
                      align="end"
                      className="md:hidden"
                      trigger={(props) => (
                        <IconButton {...props} label="Weitere Aktionen" icon={<MoreVertical className="h-4 w-4" />} variant="ghost" size="sm" />
                      )}
                      items={[
                        {
                          label: subscription.enabled ? 'Deaktivieren' : 'Aktivieren',
                          disabled: isMaintenance,
                          onSelect: () => handleToggleEnabled(subscription.subscriptionId, subscription.enabled),
                        },
                        {
                          label: 'Suche löschen',
                          danger: true,
                          className: 'delete',
                          title: isMainPrivateLocked
                            ? 'Lösche zuerst alle anderen Suchen, bevor du die Haupt-Suche löschst.'
                            : 'Löschen',
                          disabled: isMaintenance || isMainPrivateLocked,
                          onSelect: () => handleDelete(subscription.subscriptionId),
                        },
                      ]}
                    />
                  </div>
                  <SeparatedLine
                    className="mt-2 text-muted"
                    items={[
                      `Erstellt: ${new Date(subscription.createdAt).toLocaleDateString('de-DE')}`,
                      ...(subscription.expiresAt ? [`Läuft ab: ${new Date(subscription.expiresAt).toLocaleDateString('de-DE')}`] : []),
                    ]}
                  />
                </Card>
              );
            })}
          </div>
        )}

        {!isAdmin && (
          <p className="mt-4 text-sm text-muted">
            {TIER_LABEL[user?.tier ?? 'free']}: {subscriptions.length} von {currentLimit} {currentLimit === 1 ? 'Suche' : 'Suchen'} belegt.
            {(user?.tier === TIER.FREE || user?.tier === TIER.PRO) && (
              <>
                {' '}
                <Link to="/dashboard/upgrade" className={cn(inlineLinkClass, 'font-semibold')}>
                  {user?.tier === TIER.FREE ? PLANS.pro.name : PLANS.premium.name} ansehen
                </Link>
              </>
            )}
          </p>
        )}
      </div>

      <Dialog
        open={!!connectModalSubId}
        onClose={() => setConnectModalSubId(null)}
        title="Telegram-Gruppe verbinden"
        size="sm"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setConnectModalSubId(null)}>
              Abbrechen
            </Button>
            <Button type="button" variant="primary" onClick={handleConfirmConnectGroup}>
              Ja, Gruppe verbinden
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted">
          Um mehrere Suchen gleichzeitig zu nutzen, benötigt jede zusätzliche Suche eine eigene Telegram-Gruppe, in der nur du und der Bot Mitglied sind.
        </p>
        <p className="mt-3 text-sm font-medium text-ink">Hast du bereits eine neue Gruppe erstellt?</p>
        <Checkbox
          className="mt-3"
          label="Diese Info nicht mehr anzeigen"
          checked={skipGroupWarningChecked}
          onChange={(e) => setSkipGroupWarningChecked(e.target.checked)}
        />
      </Dialog>

      {/* Delete Confirmation Dialog (issue #365) */}
      {deleteConfirmState && (
        <ConfirmDialog
          title="Suche löschen"
          message={deleteConfirmState.message}
          confirmLabel="Ja, löschen"
          cancelLabel="Abbrechen"
          danger
          onConfirm={() => executeDelete(deleteConfirmState.id)}
          onCancel={() => setDeleteConfirmState(null)}
        />
      )}
    </div>
  );
};

export default DashboardHome;
